import { MainSection } from "@/styles/global";
import React from "react";
import S from "./AllEvents.styled";
import useEventsData, { IEvent } from "@/src/UseData/useEventsData";
import useNewsData, { INews } from "@/src/UseData/useNewsData";
import SearchIcon from "assets/search.svg";
import PaginationComponent from "./Pagination";
import { parseDate } from "@/src/helpers";

export interface AllEventsProps {
  title: string;
  category: string;
}

const AllEvents: React.FC<AllEventsProps> = ({ title, category }) => {
  const newsData = useNewsData();
  const [search, setSearch] = React.useState("");
  const [filteredNews, setFilteredNews] = React.useState<INews[]>(newsData);
  const [selectIsOpen, setSelectIsOpen] = React.useState(false);
  const [selectedCategory, setSelectedCategory] =
    React.useState("Alla kategorier");
  const eventData = useEventsData();

  const [filteredEvents, setFilteredEvents] =
    React.useState<IEvent[]>(eventData);

  const handleOptionClick = (option: string) => {
    setSelectedCategory(option);
    setSelectIsOpen(false);
  };

  const eventTags = eventData?.map(
    ({ eventsInfo }) => eventsInfo.tag.split("|")[0]
  );
  const eventCategory = [...new Set(eventTags)];

  const newsTags = newsData?.map(({ newsInfo }) => newsInfo.tag.split("|")[0]);
  const newsCategory = [...new Set(newsTags)];

  React.useEffect(() => {
    if (selectedCategory === "Alla kategorier") {
      setFilteredEvents(eventData);
      setFilteredNews(newsData);
    } else {
      setFilteredEvents(
        eventData.filter(
          ({ eventsInfo }) => eventsInfo.tag.split("|")[0] === selectedCategory
        )
      );
      setFilteredNews(
        newsData.filter(
          ({ newsInfo }) => newsInfo.tag.split("|")[0] === selectedCategory
        )
      );
    }
  }, [selectedCategory, eventData, newsData]);

  const handleSearch = () => {
    if (search === "") {
      setFilteredEvents(eventData);
      setFilteredNews(newsData);
    } else {
      setFilteredEvents(
        eventData.filter(({ title }) =>
          title.toLowerCase().includes(search.toLowerCase())
        )
      );
      setFilteredNews(
        newsData.filter(({ title }) =>
          title.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  };

  const removeOldEvents = (events: IEvent[]) => {
    return (
      events
        // Sortera eventen efter startdatum, ogiltiga datum hamnar sist
        .sort((a, b) => {
          const dateA = parseDate(a.eventsInfo?.datepicker);
          const dateB = parseDate(b.eventsInfo?.datepicker);

          const timeA =
            dateA instanceof Date && !isNaN(dateA.getTime())
              ? dateA.getTime()
              : Infinity;
          const timeB =
            dateB instanceof Date && !isNaN(dateB.getTime())
              ? dateB.getTime()
              : Infinity;

          return timeA - timeB;
        })
        // Filtrera bort event där slutdatumet (datepickerEnd eller datepicker) har passerat
        .filter((event: IEvent) => {
          const rawEndDate =
            event.eventsInfo?.datepickerEnd ?? event.eventsInfo?.datepicker;
          const eventEndDate = parseDate(rawEndDate);

          if (
            !(eventEndDate instanceof Date) ||
            isNaN(eventEndDate.getTime())
          ) {
            return true; // Om inget datum finns, behåll eventet (de hamnar sist pga sorteringen)
          }

          const eventEndDateOnly = new Date(
            eventEndDate.getFullYear(),
            eventEndDate.getMonth(),
            eventEndDate.getDate()
          );

          const today = new Date();
          const todayOnly = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
          );

          return eventEndDateOnly >= todayOnly;
        })
    );
  };

  return (
    <MainSection $bgColor="#F3F3F8">
      <S.Inner>
        <S.FilterWrapper>
          <S.H5>Filtrera</S.H5>
          <S.InputsWrapper>
            <S.SelectWrapper>
              <S.Select className="custom-select-container">
                <div
                  className="custom-select-header"
                  onClick={() => setSelectIsOpen(!selectIsOpen)}
                >
                  {selectedCategory === "Kategori"
                    ? "Kategori"
                    : selectedCategory}
                </div>
                {selectIsOpen && (
                  <div className="custom-select-options">
                    <option
                      value={"Alla kategorier"}
                      className="custom-select-option"
                      onClick={() => handleOptionClick("Alla kategorier")}
                    >
                      Alla kategorier
                    </option>
                    {category === "events"
                      ? eventCategory?.map((item, index) => (
                          <option
                            key={index}
                            value={item}
                            className="custom-select-option"
                            onClick={() => handleOptionClick(item)}
                          >
                            {item}
                          </option>
                        ))
                      : newsCategory?.map((item, index) => (
                          <option
                            key={index}
                            value={item}
                            className="custom-select-option"
                            onClick={() => handleOptionClick(item)}
                          >
                            {item}
                          </option>
                        ))}
                  </div>
                )}
              </S.Select>
            </S.SelectWrapper>
            <S.SearchContainer>
              <S.SearchWrapper>
                <SearchIcon />
                <S.SearchInput
                  type="text"
                  placeholder="Sök"
                  onChange={(event) => setSearch(event.target.value)}
                />
              </S.SearchWrapper>
              <S.SearchButton onClick={handleSearch}>Sök</S.SearchButton>
            </S.SearchContainer>
          </S.InputsWrapper>
        </S.FilterWrapper>
        <S.TitleWrapper>
          <S.H2>{title}</S.H2>
          <S.DetailsLarge>
            {category === "events"
              ? removeOldEvents(filteredEvents).length > 1
                ? removeOldEvents(filteredEvents).length + " evenemang"
                : removeOldEvents(filteredEvents).length + " evenemang"
              : filteredNews.length > 1
                ? filteredNews.length + " nyheter"
                : filteredNews.length + "  nyhet"}
          </S.DetailsLarge>
        </S.TitleWrapper>
        {category === "events" && (
          <PaginationComponent
            items={removeOldEvents(filteredEvents)}
            type="events"
          />
        )}
        {category === "news" && (
          <PaginationComponent items={filteredNews} type="news" />
        )}
      </S.Inner>
    </MainSection>
  );
};

export default AllEvents;
